import { ProviderData } from "@/types/provider";
import { 
  Building2, 
  Clock, 
  Percent, 
  CheckCircle2,
  ShieldCheck,
  Wallet,
  Calendar
} from "lucide-react";
import enklareLogo from "@/assets/enklare_stor.webp";
import ucLogo from "@/assets/UC.png";
import bankTransferIcon from "@/assets/banköverföring.png";

export const enklareData: ProviderData = {
  id: "enklare",
  name: "Enklare",
  slug: "enklare",
  logo: enklareLogo,
  type: "private_loan",
  
  trustpilot: {
    rating: 4.7,
    reviews: 8845,
    url: "https://se.trustpilot.com/review/enklare.se"
  },
  
  hero: {
    badges: [
      { text: "Jämför 40 långivare med en ansökan", variant: "default" }
    ],
    description: "Enklare jämför över 40 långivare för att hitta bästa privatlånet till dig. Lånebelopp från 5 000 kr upp till 800 000 kr med flexibla återbetalningsvillkor.",
    ctaText: "Ansök hos Enklare",
    ctaButtonText: "Ansök hos Enklare",
    ctaButtonVariant: "default",
    stickyCTASubtitle: "Jämför 40 långivare med en ansökan"
  },
  
  stats: [
    {
      icon: Wallet,
      value: "800 000 kr",
      label: "Maxbelopp",
      description: "Från 5 000 kr"
    },
    {
      icon: Percent,
      value: "5,07 – 24,95 %",
      label: "Effektiv ränta",
      description: "Varierar mellan långivare"
    },
    {
      icon: Calendar,
      value: "1 – 20 år",
      label: "Löptid",
      description: "Flexibel återbetalningstid"
    },
    {
      icon: Building2,
      value: "40+",
      label: "Långivare",
      description: "Jämför med en ansökan"
    },
    {
      icon: ShieldCheck,
      value: "UC",
      label: "Kreditupplysning",
      description: "UC-kontroll görs"
    },
    {
      icon: CheckCircle2,
      value: "Ja",
      label: "Betalningsanmärkningar",
      description: "Kan accepteras"
    }
  ],
  
  features: {
    pros: [
      "Kräver inte fast anställning",
      "Personlig service",
      "Enkel digital ansökan"
    ],
    cons: [
      "Inget samarbete med storbanker"
    ]
  },
  
  keyInformation: [
    {
      image: ucLogo,
      title: "Kreditupplysning",
      value: "UC"
    },
    {
      image: bankTransferIcon,
      title: "Utbetalning via",
      value: "Banköverföring"
    },
    {
      icon: Clock,
      title: "Svar på ansökan",
      value: "Samma dag"
    }
  ],
  
  termsAndConditions: [
    {
      term: "Lånebelopp",
      value: "5 000 – 800 000 kr"
    },
    {
      term: "Nominell ränta",
      value: "4,50 – 22,00 %"
    },
    {
      term: "Effektiv ränta",
      value: "5,07 – 24,95 %"
    },
    {
      term: "Löptid",
      value: "1 – 20 år"
    },
    {
      term: "Uppläggningsavgift",
      value: "Varirerar mellan långivare"
    },
    {
      term: "Lån utan UC",
      value: "Nej"
    },
    {
      term: "Svar på ansökan",
      value: "Samma dag"
    },
    {
      term: "Betalningslösning",
      value: "Banköverföring"
    }
  ],
  
  loanRequirements: [
    {
      requirement: "Legitimering",
      value: "Nej"
    },
    {
      requirement: "Folkbokförd i Sverige",
      value: "Ja, minst 1 år"
    },
    {
      requirement: "Svenskt personnummer",
      value: "Ja"
    },
    {
      requirement: "Svenskt mobilnummer",
      value: "Ja"
    },
    {
      requirement: "Svenskt kontonummer",
      value: "Ja"
    },
    {
      requirement: "Minsta årsinkomst",
      value: "120 000 kr"
    },
    {
      requirement: "Bet.anmärkning OK?",
      value: "Ja"
    },
    {
      requirement: "Ålderskrav",
      value: "18 +"
    }
  ],
  
  interestRateSection: {
    title: "Enklares ränta och avgifter",
    description: "Avgifterna varierar mellan de olika långivare som Enklare jämför. Kontrollerades senast 14 november, 2025",
    rateTable: [
      {
        label: "Lånebelopp (lägsta – högsta)",
        value: "5 000 – 800 000 kr"
      },
      {
        label: "Nominell ränta",
        value: "4,50 – 22,00 %"
      },
      {
        label: "Effektiv ränta",
        value: "5,07 – 24,95 %"
      },
      {
        label: "Uppläggningsavgift",
        value: "Varirerar mellan långivare"
      },
      {
        label: "Fakturaavgift (Pappersfaktura)",
        value: "Varirerar mellan långivare"
      },
      {
        label: "Fakturaavgift (Autogiro)",
        value: "Varirerar mellan långivare"
      }
    ]
  },
  
  payoutSection: {
    title: "När betalas lånet ut?",
    description: "Utbetalningstiden beror på vilken långivare som godkänner ditt lån, men vanligtvis sker utbetalning inom 1-4 dagar.",
    payoutTable: [
      {
        timeframe: "Beviljad före 12:00",
        duration: "1-4 dagar"
      },
      {
        timeframe: "Beviljad efter 12:00",
        duration: "1-4 dagar"
      }
    ]
  },
  
  faq: [
    {
      question: "Accepterar långivarna hos Enklare betalningsanmärkningar?",
      answer: "Ja, vissa av de 40 långivare som Enklare samarbetar med kan acceptera betalningsanmärkningar om de bedömer att du har god nuvarande betalningsförmåga. Möjligheten att bli godkänd varierar mellan långivare och beror på din ekonomiska situation."
    },
    {
      question: "Kan man låna utan UC via Enklare?",
      answer: "Nej, det går inte att låna pengar utan UC via Enklare. UC-kreditupplysning krävs för att långivarna ska kunna bedöma din kreditvärdighet."
    },
    {
      question: "Hur många långivare jämför Enklare?",
      answer: "Enklare jämför över 40 olika långivare med en enda ansökan. Detta ger dig möjlighet att hitta det bästa lånet för dina behov utan att behöva ansöka hos varje långivare separat."
    },
    {
      question: "Vad får jag för ränta hos Enklare?",
      answer: "Räntan varierar mellan de olika långivare som Enklare jämför. Den effektiva räntan ligger mellan 5,07% och 24,95% beroende på långivare, lånebelopp, återbetalningstid och din kreditvärdighet."
    },
    {
      question: "Hur lång tid tar det att få svar?",
      answer: "Du får normalt svar på din ansökan samma dag. Enklare jämför snabbt över 40 långivare och återkommer med de bästa låneförslagen."
    },
    {
      question: "Kostar det något att ansöka via Enklare?",
      answer: "Nej, det är helt kostnadsfritt att ansöka om lån via Enklare. Du betalar endast avgifter och ränta till den långivare du väljer om lånet beviljas."
    },
    {
      question: "Behöver jag ha fast anställning?",
      answer: "Nej, Enklare kräver inte fast anställning. Detta gör det möjligt för fler personer att få tillgång till lån, oavsett anställningsform."
    },
    {
      question: "Vilka banker samarbetar Enklare med?",
      answer: "Enklare samarbetar inte med de stora storbankerna, utan fokuserar på andra långivare som kan erbjuda konkurrenskraftiga villkor och mer flexibla krav."
    }
  ],
  
  customerService: {
    description: "Enklare erbjuder personlig service och hjälp via telefon och e-post.",
    phone: "010-1992509",
    phoneHours: [
      "Vardagar: 09.30-18.00",
      "Lördag: Stängt",
      "Söndag: Stängt"
    ],
    email: "kontakt@enklare.se",
    emailHours: [
      "Svarar inom 24 timmar vardagar"
    ]
  },
  
  companyInfo: {
    name: "Enklare Ekonomi Sverige AB",
    orgNumber: "556975-4814",
    address: "Vasagatan 7",
    city: "111 29 Stockholm",
    website: "https://www.enklare.se",
    fiRegisterUrl: "https://www.fi.se/sv/vara-register/foretagsregistret/details?id=127821"
  },
  
  affiliateLink: {
    linkId: "enklare-privatlan",
    trackingSource: "provider-page",
    url: "https://do.enklare.se/t/t?a=1244647179&as=2024680921&t=2&tk=1"
  },
  
  seo: {
    title: "Enklare Privatlån - Jämför 40 Långivare | Ansök Idag",
    description: "Ansök om privatlån hos Enklare och jämför 40 långivare med en ansökan. Belopp 5 000-800 000 kr, effektiv ränta 5,07-24,95%. Personlig service och svar samma dag!",
    keywords: [
      "enklare",
      "enklare lån",
      "privatlån",
      "jämför långivare",
      "lånejämförelse",
      "enklare recension"
    ]
  },
  
  schema: {
    productName: "Enklare Privatlån",
    description: "Ansök om privatlån hos Enklare och jämför 40 långivare med en ansökan. Belopp 5 000-800 000 kr, effektiv ränta 5,07-24,95%. Personlig service.",
    url: "https://klarfinans.se/sv-SE/privatlan/enklare",
    provider: "Enklare Ekonomi AB",
    interestRate: {
      min: 5.07,
      max: 24.95
    },
    minAmount: 5000,
    maxAmount: 800000,
    loanTerm: ["1 år", "20 år"],
    featureList: [
      "Jämför 40 långivare med en ansökan",
      "Kräver inte fast anställning",
      "Personlig service",
      "Enkel digital ansökan",
      "Svar samma dag",
      "Flexibla återbetalningsvillkor 1-20 år"
    ],
    aggregateRating: {
      ratingValue: 4.7,
      reviewCount: 8845
    }
  }
};
